import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faFileAlt,
  faFileImage,
  faFileVideo,
  faEllipsisV,
  faChevronRight,
  faSearch,
  faFilter,
  faCloud,
  faShareAlt,
  faClock,
  faHeart,
  faTriangleExclamation,
  faTrash,
  faChevronDown,
  faThLarge,
  faList,
  faPlus,
  faArrowLeft,
  faExclamationTriangle,
  faShield // Nuevo icono para carpetas protegidas
} from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Button, Dropdown, Form, Card, InputGroup, Table, Badge, Breadcrumb, Modal, Spinner } from 'react-bootstrap';
import FileManagerProvider, { 
  useFileManagerContext,
  SELECT_FOLDER,
  SELECT_FILE,
  SET_SEARCH_TERM,
  SET_TIME_FILTER,
  SET_FILE_MANAGER_STATE,
  FileType,
  FolderType
} from '../../../providers/FileManager';
import FileUploadModal from './FileUploadModal';
import FileDetailsModal from './FileDetailsModal';
import NewFolderModal from './NewFolderModal';
import FileSystemService from '../../../service/FileSystemService';

import { useUserContext } from '../../../layouts/UserContext';
import { getHatateToken } from '../../../helpers/utils';

// Maximum storage capacity per user in megabytes
const MAX_STORAGE = 1024; // 1024 MB = 1 GB

// Lista de directorios protegidos que no se pueden eliminar
const PROTECTED_DIRECTORIES = [
  'Public Web'
];

// Función para verificar si un directorio es protegido
const isProtectedDirectory = (folderName: string, folderPath: string): boolean => {
  // Verificar por nombre
  if (PROTECTED_DIRECTORIES.includes(folderName)) {
    return true;
  }
  
  // Verificar por ruta (para casos donde el path sea /Public Web, /Documents, etc.)
  const pathSegments = folderPath.split('/').filter(segment => segment);
  if (pathSegments.length === 1 && PROTECTED_DIRECTORIES.includes(pathSegments[0])) {
    return true;
  }
  
  return false;
};

// Componente de confirmación de eliminación
interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
  itemPath: string;
  itemType: 'file' | 'folder';
  isDeleting: boolean;
  isProtected?: boolean; // Nueva prop para indicar si es protegido
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemPath,
  itemType,
  isDeleting,
  isProtected = false
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton={!isDeleting}>
        <Modal.Title className={isProtected ? "text-warning" : "text-danger"}>
          <FontAwesomeIcon 
            icon={isProtected ? faShield : faExclamationTriangle} 
            className="me-2" 
          />
          {isProtected ? 'Directorio Protegido' : 'Confirmar eliminación'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isProtected ? (
          <div>
            <div className="alert alert-warning">
              <FontAwesomeIcon icon={faShield} className="me-2" />
              Este directorio está protegido y no puede ser eliminado.
            </div>
            <p>
              El directorio <strong>"{itemName}"</strong> es un directorio del sistema y no puede ser eliminado.
            </p>
            <div className="text-muted small">
              <div>Ruta:</div>
              <code className="d-block mt-1">{itemPath}</code>
            </div>
            <div className="alert alert-info mt-3">
              <small>
                <FontAwesomeIcon icon={faFolder} className="me-2" />
                Nota: Puedes eliminar el contenido dentro de este directorio, pero el directorio en sí permanecerá.
              </small>
            </div>
          </div>
        ) : (
          <div>
            <p>
              ¿Estás seguro que deseas eliminar este {itemType === 'file' ? 'archivo' : 'directorio'}:
              <strong className="d-block mt-2">{itemName}</strong>
            </p>
            <div className="text-muted small">
              <div>Ruta completa:</div>
              <code className="d-block mt-1">{itemPath}</code>
            </div>
            
            {itemType === 'folder' && (
              <div className="alert alert-warning mt-3">
                <small>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Advertencia: Esto eliminará todos los archivos y subdirectorios dentro de esta carpeta. Esta acción no se puede deshacer.
                </small>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
          {isProtected ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!isProtected && (
          <Button variant="danger" onClick={handleConfirm} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Eliminar {itemType === 'file' ? 'archivo' : 'carpeta'}
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

interface BreadcrumbItem {
  name: string;
  path: string;
}

const FileManagerIndex: React.FC = () => {
  return (
    <div className="file-manager-container">
      <FileManagerProvider>
        <FileManager />
      </FileManagerProvider>
    </div>
  );
};

const FileManager: React.FC = () => {
  const { fileManagerState, fileManagerDispatch } = useFileManagerContext();
  const { 
    selectedFolder, 
    expandedFolders, 
    searchTerm, 
    timeFilter,
    isUploadModalOpen,
    isDetailsModalOpen,
    isNewFolderModalOpen,
    selectedFile
  } = fileManagerState;
  
  // Add view type state with 'grid' as default
  const [viewType, setViewType] = useState<'list' | 'grid'>('grid');
  // State for the current path and content
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ name: '/', path: '/' }]);
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [files, setFiles] = useState<FileType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estado para el modal de confirmación de eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'file' | 'folder', item: any } | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  
  const {
    user
  } = useUserContext();

  // Helper function to get the icon for a file based on its extension
  const getFileIcon = (extension: string): React.ComponentProps<typeof FontAwesomeIcon>['icon'] => {
    switch (extension.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return faFileImage;
      case 'mp4':
      case 'mov':
      case 'avi':
        return faFileVideo;
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return faFileAlt;
      default:
        return faFile;
    }
  };

  // Format file size to readable format
  const formatFileSize = (size: number): string => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    } else if (size < 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  // Handler for folder toggle in sidebar
  const handleFolderToggle = (folderId: string) => {
    const newExpandedFolders = expandedFolders.includes(folderId)
      ? expandedFolders.filter(id => id !== folderId)
      : [...expandedFolders, folderId];
    
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { expandedFolders: newExpandedFolders }
    });
  };

  // Handler for folder selection from sidebar
  const handleFolderSelect = (folderId: string, folderPath: string) => {
    fileManagerDispatch({
      type: SELECT_FOLDER,
      payload: folderId
    });
    
    // Navigate to the folder path
    if (folderPath) {
      navigateToFolder(folderPath);
    }
  };

  // Handler for file selection
  const handleFileSelect = (file: FileType) => {
    fileManagerDispatch({
      type: SELECT_FILE,
      payload: file
    });
  };

  // Set search term
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    fileManagerDispatch({
      type: SET_SEARCH_TERM,
      payload: e.target.value
    });
  };

  // Set time filter
  const handleSetTimeFilter = (filter: string) => {
    fileManagerDispatch({
      type: SET_TIME_FILTER,
      payload: filter
    });
  };

  // Open modal handlers
  const openUploadModal = () => {
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { isUploadModalOpen: true }
    });
  };

  const openNewFolderModal = () => {
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { isNewFolderModalOpen: true }
    });
  };

  // Handler para iniciar el proceso de eliminación de un archivo
  const handleConfirmFileDelete = (file: FileType, e: React.MouseEvent) => {
    e.stopPropagation(); // Previene que se active la selección del archivo
    console.log('Iniciando eliminación de archivo:', file);
    setItemToDelete({ type: 'file', item: file });
    setIsDeleteModalOpen(true);
  };

  // Handler para iniciar el proceso de eliminación de un directorio
  const handleConfirmFolderDelete = (folder: FolderType, e: React.MouseEvent) => {
    e.stopPropagation(); // Previene que se navegue al directorio
    console.log('Iniciando eliminación de carpeta:', folder);
    
    // Verificar si la carpeta está protegida
    const isProtected = isProtectedDirectory(folder.name, folder.path);
    
    if (isProtected) {
      console.log('Carpeta protegida detectada:', folder.name);
    }
    
    setItemToDelete({ type: 'folder', item: folder });
    setIsDeleteModalOpen(true);
  };

  // Handler para ejecutar la eliminación una vez confirmada
  const handleDeleteConfirmed = async () => {
    if (!itemToDelete) return;
    
    // Verificar si es un directorio protegido
    if (itemToDelete.type === 'folder') {
      const isProtected = isProtectedDirectory(itemToDelete.item.name, itemToDelete.item.path);
      if (isProtected) {
        console.log('Intento de eliminar directorio protegido bloqueado');
        return; // No hacer nada si es protegido
      }
    }
    
    try {
      setIsDeleting(true);
      
      if (itemToDelete.type === 'file') {
        // Para archivos, usar la ruta proporcionada directamente por el objeto file.path
        // Esto asegura que usamos la misma ruta que el backend conoce
        const filePath = itemToDelete.item.path;
        console.log(`Attempting to delete file with path: ${filePath}`);
        await FileSystemService.deleteFile(filePath);
      } else {
        // Para carpetas, usar la ruta proporcionada directamente por el objeto folder.path
        const folderPath = itemToDelete.item.path;
        console.log(`Attempting to delete folder with path: ${folderPath}`);
        await FileSystemService.deleteDirectory(folderPath);
      }
      
      // Actualiza la lista después de eliminar solo si el usuario está autenticado
      if (user) {
        await loadFilesAndFolders(currentPath);
      }
      setError(null);
    } catch (err) {
      console.error(`Error al eliminar ${itemToDelete.type}:`, err);
      setError(`Error al eliminar ${itemToDelete.type}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  // Cerrar el modal de eliminación
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  // Close modal handlers
  const closeUploadModal = () => {
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { isUploadModalOpen: false }
    });
    
    // Refresh the file list after upload only if user is authenticated
    if (user) {
      loadFilesAndFolders(currentPath);
    }
  };

  const closeDetailsModal = () => {
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { isDetailsModalOpen: false, selectedFile: null }
    });
    
    // Refresh the file list after details modal is closed only if user is authenticated
    if (user) {
      loadFilesAndFolders(currentPath);
    }
  };

  const closeNewFolderModal = () => {
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: { isNewFolderModalOpen: false }
    });
    
    // Refresh the file list after new folder is created only if user is authenticated
    if (user) {
      loadFilesAndFolders(currentPath);
    }
  };

  // Toggle view type
  const toggleViewType = (type: 'list' | 'grid') => {
    setViewType(type);
  };

  // Effect to load files and handle session changes
  useEffect(() => {
    // Wait a bit for user context to be fully loaded
    const timeoutId = setTimeout(() => {
      if (user) {
        // User is logged in, load files
        loadFilesAndFolders(currentPath);
      } else {
        // User is not logged in or session expired, clear all data
        clearFileManagerState();
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [currentPath, user]);

  // Effect to handle user session changes and clear data on logout
  useEffect(() => {
    if (!user) {
      // Clear all file manager state when user logs out
      clearFileManagerState();
    }
  }, [user]);

  // Function to clear all file manager state
  const clearFileManagerState = () => {
    setFolders([]);
    setFiles([]);
    setCurrentPath('/');
    setBreadcrumbs([{ name: 'Root', path: '/' }]);
    setError(null);
    setIsLoading(false);
    setIsDeleteModalOpen(false);
    setItemToDelete(null);
    setIsDeleting(false);
    
    // Clear file manager context state
    fileManagerDispatch({
      type: SET_FILE_MANAGER_STATE,
      payload: {
        selectedFolder: null,
        selectedFile: null,
        expandedFolders: [],
        searchTerm: '',
        timeFilter: 'All time',
        isUploadModalOpen: false,
        isDetailsModalOpen: false,
        isNewFolderModalOpen: false
      }
    });
  };

  // Function to load files and folders from the specified path
  const loadFilesAndFolders = async (path: string): Promise<void> => {
    // Don't load if user is not authenticated
    if (!user) {
      console.log('User not authenticated, skipping file load');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Loading files and folders from path:', path, 'for user:', user._id);
      
      const HatateToken = getHatateToken();
      
      // Call the FileSystemService to read directory
      const result = await FileSystemService.readDirectory(path);
      
      // Build breadcrumbs
      const pathSegments = path.split('/').filter(segment => segment);
      const newBreadcrumbs: BreadcrumbItem[] = [{ name: '/', path: '/' }];
      let cumulativePath = '';
      
      for (const segment of pathSegments) {
        cumulativePath += `/${segment}`;
        newBreadcrumbs.push({
          name: segment,
          path: cumulativePath
        });
      }
      
      setFolders(result.folders);
      setFiles(result.files);
      setBreadcrumbs(newBreadcrumbs);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading files:', err);
      setError(`Error loading files: ${err instanceof Error ? err.message : String(err)}`);
      setIsLoading(false);
    }
  };

  // Navigation function
  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
  };

  // Get recent files - sorted by last modified date
  const recentFiles = [...files]
    .sort((a, b) => {
        // Check if lastModified exists and handle undefined cases
        const timeA = a.lastModified 
            ? (a.lastModified instanceof Date 
                ? a.lastModified.getTime() 
                : typeof a.lastModified === 'number' 
                    ? a.lastModified 
                    : new Date(a.lastModified).getTime())
            : 0; // Default to 0 if undefined
        
        const timeB = b.lastModified 
            ? (b.lastModified instanceof Date 
                ? b.lastModified.getTime() 
                : typeof b.lastModified === 'number' 
                    ? b.lastModified 
                    : new Date(b.lastModified).getTime())
            : 0; // Default to 0 if undefined
        
        return timeB - timeA;
    })
    .slice(0, 4);

  // Calculate total used storage
  const totalStorage = MAX_STORAGE * 1024 * 1024; // Convert MB to bytes
  const usedStorage = files.reduce((total, file) => total + (file.size || 0), 0);
  const usedStoragePercentage = Math.round((usedStorage / totalStorage) * 100);

  // Render the list view
  const renderListView = (filesArray: FileType[]): JSX.Element => (
    <div className="table-responsive file-table-container">
      <Table className="file-table table-hover border-bottom">
        <thead className="bg-light">
          <tr>
            <th className="py-2" style={{ width: '40%' }}>Name</th>
            <th className="py-2" style={{ width: '15%' }}>Size</th>
            <th className="py-2" style={{ width: '15%' }}>Type</th>
            <th className="py-2" style={{ width: '20%' }}>Last Modified</th>
            <th className="py-2 text-end" style={{ width: '10%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filesArray.map(file => (
            <tr 
              key={file.id} 
              onClick={() => handleFileSelect(file)} 
              className="cursor-pointer file-table-row"
            >
              <td className="py-2">
                <div className="d-flex align-items-center">
                  <div className="me-3 text-700">
                    <FontAwesomeIcon 
                      icon={getFileIcon(file.extension || '')} 
                      className={
                        ['jpg', 'jpeg', 'png'].includes(file.extension?.toLowerCase() || '') 
                          ? 'text-success' 
                          : 'text-primary'
                      } 
                    />
                  </div>
                  <div className="file-name-container">
                    <span className="file-name text-truncate">
                      {file.name}
                    </span>
                    {file.id === '102' && (
                      <Badge bg="info" className="ms-2 file-badge">
                        New
                      </Badge>
                    )}
                  </div>
                </div>
              </td>
              <td className="py-2 file-size">
                {formatFileSize(file.size || 0)}
              </td>
              <td className="py-2 file-type">
                {file.extension?.toUpperCase() || 'Unknown'}
              </td>
              <td className="py-2 file-modified-date">
                {(() => {
                  try {
                    // Handle undefined lastModified
                    if (!file.lastModified) {
                      return 'Unknown Date';
                    }
                    
                    // Convert lastModified to a date object if it's not already one
                    const dateObj = file.lastModified instanceof Date 
                      ? file.lastModified 
                      : typeof file.lastModified === 'number'
                        ? new Date(file.lastModified)
                        : new Date(file.lastModified);
                    
                    return dateObj.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                  } catch (error) {
                    console.error('Error parsing date:', error);
                    return 'Unknown Date';
                  }
                })()}
              </td>
              <td className="text-end py-2 file-actions">
                <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Toggle 
                    variant="link" 
                    size="sm" 
                    className="px-2 text-muted file-action-toggle" 
                    id={`dropdown-${file.id}`}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item>Download</Dropdown.Item>
                    <Dropdown.Item>Rename</Dropdown.Item>
                    <Dropdown.Item>Share</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item 
                      className="text-danger" 
                      onClick={(e) => handleConfirmFileDelete(file, e)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  // Render the grid view (card view)
  const renderGridView = (filesArray: FileType[]): JSX.Element => (
    <Row className="g-3 file-grid-view">
      {filesArray.map(file => (
        <Col md={6} xl={3} key={file.id}>
          <Card 
            className="file-card h-100 overflow-hidden cursor-pointer" 
            onClick={() => handleFileSelect(file)}
          >
            {/* File thumbnail/icon */}
            <div className="file-card-preview position-relative">
              {['jpg', 'jpeg', 'png'].includes(file.extension?.toLowerCase() || '') ? (
                <>
                  <Card.Img 
                    variant="top" 
                    src="/api/placeholder/400/320" 
                    alt={file.name}
                    className="file-preview-image"
                  />
                  {file.id === '102' ? (
                    <Badge bg="info" className="file-badge position-absolute top-0 end-0 m-2">
                      UPLOAD 6 MIN AGO
                    </Badge>
                  ) : (
                    <Badge bg="warning" className="file-badge position-absolute top-0 end-0 m-2">
                      EDITED 5 MIN AGO
                    </Badge>
                  )}
                </>
              ) : ['mp4', 'mov', 'avi'].includes(file.extension?.toLowerCase() || '') ? (
                <div className="file-preview-video bg-dark d-flex align-items-center justify-content-center">
                  <div className="video-icon-wrapper">
                    <FontAwesomeIcon icon={faFileVideo} className="text-white fs-3" />
                  </div>
                  {file.id === '102' ? (
                    <Badge bg="info" className="file-badge position-absolute top-0 end-0 m-2">
                      UPLOAD 6 MIN AGO
                    </Badge>
                  ) : (
                    <Badge bg="warning" className="file-badge position-absolute top-0 end-0 m-2">
                      EDITED 5 MIN AGO
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="file-preview-default bg-light d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon 
                    icon={getFileIcon(file.extension || '')} 
                    className="text-primary fs-1"
                  />
                  {file.id === '102' ? (
                    <Badge bg="info" className="file-badge position-absolute top-0 end-0 m-2">
                      UPLOAD 6 MIN AGO
                    </Badge>
                  ) : (
                    <Badge bg="warning" className="file-badge position-absolute top-0 end-0 m-2">
                      EDITED 5 MIN AGO
                    </Badge>
                  )}
                </div>
              )}
            </div>
            
            {/* File details */}
            <Card.Body className="file-card-body p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div className="file-name-container">
                  <Card.Title className="file-name fs--1 text-truncate mb-0">
                    {file.name}
                  </Card.Title>
                </div>
                <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Toggle 
                    variant="link" 
                    size="sm" 
                    className="px-2 text-muted file-action-toggle" 
                    id={`dropdown-${file.id}`}
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item>Download</Dropdown.Item>
                    <Dropdown.Item>Rename</Dropdown.Item>
                    <Dropdown.Item>Share</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item 
                      className="text-danger" 
                      onClick={(e) => handleConfirmFileDelete(file, e)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <p className="file-size fs--2 mb-0 text-muted mt-1">
                {formatFileSize(file.size || 0)}
              </p>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <div className="file-manager-container">
      {/* Breadcrumb navigation */}
      <div className="file-manager-header">
        <Breadcrumb>
          {breadcrumbs.map((crumb, index) => (
            <Breadcrumb.Item 
              key={index} 
              active={index === breadcrumbs.length - 1}
              onClick={() => navigateToFolder(crumb.path)}
              linkAs="span"
              className="cursor-pointer"
            >
              {crumb.name}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <h2 className="mt-3 mb-4 fw-bold text-body-emphasis">File Manager</h2>
      </div>

      <Row>
        {/* Left Sidebar */}
        <Col xs={12} md={3} xl={2} className="file-manager-sidebar">
          <div className="mb-4">
            <h5 className="mb-3 text-body-emphasis">My Files</h5>
            
            {/* Navigation Tree */}
            <div className="nav-folder-tree">
              <div 
                className="nav-link d-flex align-items-center py-2 cursor-pointer"
                onClick={() => handleFolderSelect('root', '/')}
              >
                <FontAwesomeIcon
                  icon={expandedFolders.includes('root') ? faChevronDown : faChevronRight}
                  className="me-2 text-500 fs--2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFolderToggle('root');
                  }}
                />
                <span className="d-flex align-items-center">
                  <span className="text-700 me-2 d-inline-block">
                    <FontAwesomeIcon icon={faFolder} className="text-primary" />
                  </span>
                  <span className="text-body-emphasis">/</span>
                </span>
              </div>
              
              {expandedFolders.includes('root') && (
                <div className="ms-3">
                  {['Public Web', 'Documents', 'Images', 'Videos'].map((folderName) => {
                    const isProtected = PROTECTED_DIRECTORIES.includes(folderName);
                    return (
                      <div 
                        key={folderName}
                        className="nav-link d-flex align-items-center py-2 cursor-pointer"
                        onClick={() => handleFolderSelect(folderName.toLowerCase(), `/${folderName}`)}
                      >
                        <span className="d-flex align-items-center">
                          <span className="text-700 me-2 d-inline-block position-relative">
                            <FontAwesomeIcon icon={faFolder} className="text-primary" />
                            {isProtected && (
                              <FontAwesomeIcon 
                                icon={faShield} 
                                className="text-warning position-absolute"
                                style={{ 
                                  fontSize: '0.6em', 
                                  top: '-2px', 
                                  right: '-6px',
                                  background: 'white',
                                  borderRadius: '50%',
                                  padding: '1px'
                                }}
                                title="Directorio protegido"
                              />
                            )}
                          </span>
                          <span className="text-body-emphasis">{folderName}</span>
                          {isProtected && (
                            <Badge bg="warning" className="ms-2" style={{ fontSize: '0.6em' }}>
                              Protegido
                            </Badge>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="nav flex-column fs--1 mt-3">
              {[
                { icon: faClock, text: 'Recent' },
                { icon: faTrash, text: 'Trash' },
              ].map((item) => (
                <div key={item.text} className="nav-item">
                  <div className="nav-link d-flex align-items-center py-2 cursor-pointer">
                    <span className="text-700 me-2 d-inline-block">
                      <FontAwesomeIcon icon={item.icon} />
                    </span>
                    <span className="text-body-emphasis">{item.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Storage Details */}
          <div className="mt-4 storage-details">
            <h5 className="mb-3 fs-0 text-body-emphasis">Storage</h5>
            <div className="w-100 storage-bar" style={{ height: '0.5rem', backgroundColor: '#edf2f9' }}>
              <div 
                className="h-100 rounded-pill bg-primary" 
                style={{ width: `${usedStoragePercentage}%` }}
              ></div>
            </div>
            <p className="fs--1 text-body-tertiary mt-2">
              Used: {formatFileSize(usedStorage)} ({usedStoragePercentage}%) of {MAX_STORAGE} MB
            </p>
          </div>
        </Col>
        
        {/* Main Content */}
        <Col xs={12} md={9} xl={10} className="file-manager-content">
          <div className="file-manager-toolbar">
            <div>
              <Button 
                variant="primary" 
                className="me-2"
                onClick={openUploadModal}
              >
                <FontAwesomeIcon icon={faCloud} className="me-2" />
                Upload
              </Button>
              <Button 
                variant="outline-primary"
                onClick={openNewFolderModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                New Folder
              </Button>
            </div>

            <div className="d-flex align-items-center">
              <InputGroup className="me-2">
                <InputGroup.Text className="bg-white border-end-0">
                  <FontAwesomeIcon icon={faSearch} className="text-body-tertiary" />
                </InputGroup.Text>
                <Form.Control
                  type="search"
                  placeholder="Search files"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="border-start-0"
                />
              </InputGroup>
              
              <Dropdown align="end" className="me-2">
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  {timeFilter}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleSetTimeFilter('Last 7 days')}>
                    Last 7 days
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSetTimeFilter('Last 30 days')}>
                    Last 30 days
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSetTimeFilter('All time')}>
                    All time
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              {/* View Toggle Buttons */}
              <div className="btn-group">
                <Button 
                  variant={viewType === 'list' ? 'primary' : 'outline-secondary'} 
                  size="sm"
                  onClick={() => toggleViewType('list')}
                >
                  <FontAwesomeIcon icon={faList} />
                </Button>
                <Button 
                  variant={viewType === 'grid' ? 'primary' : 'outline-secondary'} 
                  size="sm"
                  onClick={() => toggleViewType('grid')}
                >
                  <FontAwesomeIcon icon={faThLarge} />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Loading and Error States */}
          {!user ? (
            <div className="file-manager-empty-state">
              <div className="mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning" />
              </div>
              <h5>Authentication Required</h5>
              <p className="text-muted">Please log in to access your files</p>
            </div>
          ) : isLoading ? (
            <div className="file-manager-empty-state">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading files...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>Error loading files</strong>
              </div>
              <p className="mb-2">{error}</p>
              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => loadFilesAndFolders(currentPath)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                  Retry
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
              </div>
            </div>
          ) : null}
          
          {/* Directory Content */}
          {user && !isLoading && !error && (
            <div className="file-manager-directory">
              {/* Folders Section */}
              {folders.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3">Folders</h5>
                  <Row className="g-3">
                    {folders.map(folder => {
                      const isProtected = isProtectedDirectory(folder.name, folder.path);
                      return (
                        <Col md={6} lg={4} xl={3} key={folder.id}>
                          <Card 
                            className="file-card h-100 cursor-pointer" 
                            onClick={() => navigateToFolder(folder.path)}
                          >
                            <Card.Body className="p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div className="me-3 text-primary position-relative">
                                    <FontAwesomeIcon icon={faFolder} size="2x" />
                                    {isProtected && (
                                      <FontAwesomeIcon 
                                        icon={faShield} 
                                        className="text-warning position-absolute"
                                        style={{ 
                                          fontSize: '0.8em', 
                                          top: '-4px', 
                                          right: '-8px',
                                          background: 'white',
                                          borderRadius: '50%',
                                          padding: '2px'
                                        }}
                                        title="Directorio protegido - No se puede eliminar"
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <h6 className="mb-1 d-flex align-items-center">
                                      {folder.name}
                                      {isProtected && (
                                        <Badge bg="warning" className="ms-2" style={{ fontSize: '0.7em' }}>
                                          <FontAwesomeIcon icon={faShield} className="me-1" />
                                          Protegido
                                        </Badge>
                                      )}
                                    </h6>
                                    <p className="text-muted mb-0 fs--1">Folder</p>
                                  </div>
                                </div>
                                <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                                  <Dropdown.Toggle 
                                    variant="link" 
                                    size="sm" 
                                    className="px-2 text-muted file-action-toggle" 
                                    id={`dropdown-folder-${folder.id}`}
                                  >
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item>Rename</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item 
                                      className={isProtected ? "text-muted" : "text-danger"}
                                      onClick={(e) => handleConfirmFolderDelete(folder, e)}
                                      disabled={isProtected}
                                    >
                                      <FontAwesomeIcon 
                                        icon={isProtected ? faShield : faTrash} 
                                        className="me-2" 
                                      />
                                      {isProtected ? 'Protegido' : 'Delete'}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
              
              {/* Files Section */}
              {files.length > 0 ? (
                <div className="mb-5">
                  <h5 className="mb-3">Files</h5>
                  {viewType === 'list' 
                    ? renderListView(files)
                    : renderGridView(files)
                  }
                </div>
              ) : (
                !folders.length && (
                  <div className="file-manager-empty-state">
                    <div className="mb-3">
                      <FontAwesomeIcon icon={faFolder} size="3x" className="text-muted" />
                    </div>
                    <h5>No files found</h5>
                    <p className="text-muted">This folder is empty</p>
                  </div>
                )
              )}
              
              {/* Recent Files Section */}
              {currentPath === '/' && recentFiles.length > 0 && !isLoading && !error && (
                <div className="mb-5">
                  <h5 className="mb-3">Recent Files</h5>
                  {viewType === 'list' 
                    ? renderListView(recentFiles)
                    : renderGridView(recentFiles)
                  }
                  <div className="text-center mt-3">
                    <Button variant="outline-secondary" size="sm">View More</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
      
      {/* Modals */}
      {isUploadModalOpen && (
        <FileUploadModal onClose={closeUploadModal} currentPath={currentPath} />
      )}
      
      {isDetailsModalOpen && selectedFile && (
        <FileDetailsModal onClose={closeDetailsModal} />
      )}
      
      {isNewFolderModalOpen && (
        <NewFolderModal onClose={closeNewFolderModal} currentPath={currentPath} />
      )}
      
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && itemToDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteConfirmed}
          itemName={itemToDelete.item.name}
          itemPath={itemToDelete.item.path}
          itemType={itemToDelete.type}
          isDeleting={isDeleting}
          isProtected={
            itemToDelete.type === 'folder' 
              ? isProtectedDirectory(itemToDelete.item.name, itemToDelete.item.path)
              : false
          }
        />
      )}
    </div>
  );
};

export default FileManagerIndex;